import React, { useState } from 'react';
import { Globe, Star, ChevronRight } from 'lucide-react';
import { useCountry } from '../../contexts/CountryContext';
import RateAppModal from './RateAppModal';

/**
 * Home banner shown to users outside the supported markets. It explains the
 * service isn't available yet, lets registered users explore (but not book),
 * and offers a rate/suggest-a-feature action. Live-deployment only.
 */
const CountryRestrictionBanner = ({ user }) => {
  const ctx = useCountry();
  const [showRate, setShowRate] = useState(false);

  if (!ctx || ctx.loading) return null;
  // Only enforce on the live site (with a test toggle handled in context)
  if (!ctx.isLive) return null;
  if (ctx.isSupportedCountry) return null;

  const supported = (ctx.supportedCountries || []).map((c) => `${c.flag} ${c.name}`).join(', ');

  return (
    <>
      <div
        className="mx-4 my-3 rounded-2xl border border-amber-200 bg-amber-50 p-4"
        data-testid="country-restriction-banner"
      >
        <div className="flex items-start gap-3">
          <span className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Globe className="w-5 h-5 text-amber-600" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-900">Ride Yachangu isn't available in your country yet</p>
            <p className="text-xs text-amber-700 mt-0.5">
              We currently operate in {supported}. You can explore the app, but booking a ride is disabled in your region for now.
            </p>
            <button
              onClick={() => setShowRate(true)}
              data-testid="restriction-rate-btn"
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-800 bg-white border border-amber-300 rounded-full px-3 py-1.5 active:scale-95 transition-transform"
            >
              <Star className="w-4 h-4" /> Rate us & suggest a feature <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
      {showRate && <RateAppModal user={user} onClose={() => setShowRate(false)} />}
    </>
  );
};

// Small helper other screens can use to know if booking should be blocked
export const useBookingAllowed = () => {
  const ctx = useCountry();
  if (!ctx || ctx.loading) return true;      // don't block while resolving
  if (!ctx.isLive) return true;              // preview: never block
  return ctx.isSupportedCountry;
};

export default CountryRestrictionBanner;
